import { useBuild } from "~/components/build/Show/lib/BuildContext";
import BuildMessage from "~/components/build/Message";
import { Commit } from "~/components/build/Header/Commit";
import { PullRequest } from "~/components/build/Header/PullRequest";
import UserAvatar from "~/components/shared/UserAvatar";
import Icon from "~/components/shared/Icon";
import Emojify from "~/components/shared/Emojify";
import { capitalize } from "lodash";
import { useNavigate } from "react-router-dom";
import { Build } from "~/components/build/Show/lib/types";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { Branch } from "~/components/build/Header/Branch";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";

const labelsForSource: { [key: string]: string } = {
  ui: "website",
  webhook: "webhook",
  api: "API",
  trigger_job: "pipeline",
  schedule: "schedule",
};

const BuildSource = ({ build }: { build: Build }) => {
  const label = labelsForSource[build.source] || "unknown";

  if (build.rebuiltFrom) {
    return (
      <div className="text-sm">
        Rebuild of{" "}
        <a
          href={build.rebuiltFrom.url}
          className="font-medium text-inherit hover:text-inherit focus:text-inherit"
        >
          #{build.rebuiltFrom.number}
        </a>{" "}
        from {label}
      </div>
    );
  }

  if (build.triggeredFrom) {
    if (build.triggeredFrom.project && build.triggeredFrom.build) {
      return (
        <span>
          Triggered from{" "}
          <a
            href={build.triggeredFrom.url}
            className="font-medium text-inherit hover:text-inherit focus:text-inherit"
          >
            <Emojify text={build.triggeredFrom.project.name} /> (#
            {build.triggeredFrom.build.number})
          </a>
        </span>
      );
    }
    // Most likely the pipeline is one this user doesn't have access to.
    return <span>Triggered from pipeline</span>;
  }

  if (build.pipelineTrigger) {
    return (
      <span>
        Triggered from {capitalize(label)} via{" "}
        <a
          href={build.pipelineTrigger.url}
          className="font-medium text-inherit hover:text-inherit focus:text-inherit"
        >
          {build.pipelineTrigger.label}
        </a>
      </span>
    );
  }

  return <span>{capitalize(label)}</span>;
};

/**
 * Sums annotation counts by style across multiple count maps.
 * Handles overlapping keys (e.g. "error" in both) by adding their values.
 */
function mergeAnnotationCountsByStyle(...maps: Record<string, number>[]): Record<string, number> {
  const merged: Record<string, number> = {};
  for (const map of maps) {
    for (const [style, count] of Object.entries(map)) {
      merged[style] = (merged[style] || 0) + count;
    }
  }
  return merged;
}

// Map annotation styles to colors for badges
const annotationBadgeColors: { [key: string]: string } = {
  error: "bg-red-100 text-red-700 border border-red-200",
  warning: "bg-orange-100 text-orange-700 border border-orange-200",
  info: "bg-blue-100 text-blue-700 border border-blue-200",
  success: "bg-lime-100 text-lime-700 border border-lime-200",
  default: "bg-gray-100 text-gray-700 border border-gray-200",
};

export default function BuildContextCard() {
  const { build } = useBuild();
  const navigate = useNavigate();

  // Calculate annotation counts, including job annotations
  const counts = getAnnotationCounts(build);
  const totalAnnotationCount = counts.totalCount;

  const annotationCountsByStyle = mergeAnnotationCountsByStyle(
    build.annotationCountsByStyle || {},
    build.statistics?.jobAnnotationCountsByStyle || {},
  );

  const presentStyles = Object.keys(annotationCountsByStyle).filter(
    (key) => annotationCountsByStyle[key] > 0,
  );

  if (!build.source && !build.commitId && !build.authorName) {
    return null;
  }

  return (
    <div
      className="shadow-depth-200 flex h-full flex-col rounded-md bg-white p-3"
      data-testid="trigger-source-card"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="my-0 inline max-w-[50%] truncate text-base font-semibold sm:text-lg">
          Build #{build.number}
        </h2>
      </div>

      <div className="flex flex-auto flex-col gap-y-3">
        <div className="col-span-2">
          <BuildMessage
            message={build.message}
            className="text-charcoal-700 line-clamp-3 text-base font-medium"
          />
        </div>

        <div className="text-sm">
          <div className="flex flex-wrap gap-4">
            {build.commitId && (
              <div className="min-w-[calc(50%-8px)] flex-1">
                <span className="mb-0.5 block text-gray-900">Commit</span>
                <span className="text-charcoal-700 inline-flex items-center gap-1">
                  <Commit {...build} className="peer inline-flex pr-6 text-sm font-medium" />
                </span>
              </div>
            )}

            <div className="min-w-[calc(50%-8px)] flex-1">
              <span className="mb-0.5 block text-gray-900">
                {build.mergeQueueBaseBranch ? "Merge queue" : "Branch"}
              </span>
              <Branch {...build} className="max-w-full pr-6" />
            </div>
          </div>
        </div>

        {build.pullRequest && (
          <div className="col-span-2">
            <span className="mb-0.5 block text-gray-900">
              {build.project.provider.id === "gitlab" ? "Merge request" : "Pull request"}
            </span>
            <PullRequest {...build} className="text-sm font-medium" />
          </div>
        )}

        {/* Divider */}
        <div className="col-span-2 border-b" />

        <div className="text-sm">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[calc(50%-8px)] flex-1">
              <span className="mb-0.5 block text-gray-900">Created</span>

              <div className="text-charcoal-700">
                <FriendlyTime value={build.createdAt} capitalized={true} />
              </div>
            </div>

            {build.authorName && (
              <div className="min-w-[calc(50%-8px)] flex-1">
                <span className="mb-0.5 block text-gray-900">Author</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  <UserAvatar
                    size="SMALL"
                    user={{
                      name: build.authorName || "Unknown",
                      avatar: { url: build.authorAvatar || "" },
                    }}
                  />
                  <span className="text-sm">{build.authorName || "System"}</span>
                  {build.creatorVerified && (
                    <span className="build-verified ml-0">
                      <Icon icon="check" className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="min-w-[calc(50%-8px)] flex-1">
              <span className="mb-0.5 block text-gray-900">Source</span>
              <div className="text-charcoal-700 flex items-center gap-1">
                <BuildSource build={build} />
              </div>
            </div>
          </div>
        </div>
        {totalAnnotationCount > 0 && (
          <div className="mt-auto border-t pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {presentStyles.map((style) => (
                  <span
                    key={style}
                    className={`rounded-sm px-2 py-1 text-xs font-medium ${annotationBadgeColors[style] || annotationBadgeColors.default}`}
                  >
                    {annotationCountsByStyle[style]}
                  </span>
                ))}
                <span className="text-sm font-medium">
                  {totalAnnotationCount} annotation
                  {totalAnnotationCount !== 1 ? "s" : ""}
                </span>
              </div>
              <button
                onClick={() => navigate("../annotations", { relative: "path" })}
                className="transition-color text-charcoal-700 hover:text-charcoal-600 focus:text-charcoal-600 active:text-charcoal-600 shadow-xs flex items-center gap-1 rounded-sm border border-slate-400 bg-white px-1.5 py-0.5 text-sm font-medium no-underline hover:bg-gray-100"
              >
                View all <Icon icon="arrow-right" className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
